import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Users, 
  Loader2,
  AlertCircle,
  ChevronLeft,
  TrendingUp,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  BookOpen
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function InstructorStudents() {
  const { courseId } = useParams();
  const { token } = useAuth();
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(courseId || null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const res = await fetch(`${API_URL}/api/portal/courses/instructor/my`, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include'
        });
        
        if (res.ok) {
          const data = await res.json();
          setCourses(data.courses || []);
          if (!selectedCourse && data.courses?.length > 0) {
            setSelectedCourse(data.courses[0].course_id);
          }
        }
      } catch (err) {
        console.error('Error fetching courses:', err);
      }
    };
    
    if (token) {
      fetchCourses();
    }
  }, [token]);

  useEffect(() => {
    const fetchStudents = async () => {
      if (!selectedCourse) {
        setLoading(false);
        return;
      }
      
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/portal/instructor/students/${selectedCourse}`, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include'
        });
        
        if (res.ok) {
          const data = await res.json();
          setStudents(data.students || []);
        } else {
          setError('Failed to load students');
        }
        setLoading(false);
      } catch (err) {
        console.error('Error fetching students:', err);
        setError('Failed to load students');
        setLoading(false);
      }
    };
    
    if (selectedCourse && token) {
      fetchStudents();
    }
  }, [selectedCourse, token]);

  const getProgressColor = (progress) => {
    if (progress >= 75) return 'text-emerald-600 bg-emerald-50';
    if (progress >= 50) return 'text-blue-600 bg-blue-50';
    if (progress >= 25) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  if (loading && !courses.length) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="instructor-students">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Students</h1>
          <p className="text-gray-600">Monitor student progress and performance</p>
        </div>
      </div>

      {/* Course Selector */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Select Course</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {courses.map((course) => (
              <Button
                key={course.course_id}
                variant={selectedCourse === course.course_id ? 'default' : 'outline'}
                className={selectedCourse === course.course_id ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                onClick={() => setSelectedCourse(course.course_id)}
              >
                <BookOpen className="w-4 h-4 mr-2" />
                {course.code}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Students List */}
      {loading ? (
        <div className="flex items-center justify-center h-32">
          <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
        </div>
      ) : error ? (
        <Card>
          <CardContent className="py-12 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <p className="text-gray-600">{error}</p>
          </CardContent>
        </Card>
      ) : students.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">No students enrolled in this course</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {students.map((student) => (
            <Card key={student.user_id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold text-lg">
                      {student.name?.charAt(0)?.toUpperCase() || 'S'}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{student.name}</h3>
                      <p className="text-sm text-gray-500">{student.email}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-6">
                    {/* Course Progress */}
                    <div className="text-center">
                      <div className={`text-lg font-bold px-3 py-1 rounded-lg ${getProgressColor(student.course_progress || 0)}`}>
                        {student.course_progress || 0}%
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Progress</p>
                    </div>
                    
                    {/* Assignments */}
                    <div className="text-center">
                      <div className="flex items-center gap-1">
                        <CheckCircle className="w-4 h-4 text-emerald-500" />
                        <span className="font-semibold">{student.completed_assignments || 0}</span>
                        <span className="text-gray-400">/</span>
                        <span className="text-gray-600">{student.total_assignments || 0}</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Assignments</p>
                    </div>
                    
                    {/* Average Grade */}
                    <div className="text-center">
                      <div className="font-semibold text-lg">
                        {student.average_grade ? `${Math.round(student.average_grade)}%` : 'N/A'}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Avg Grade</p>
                    </div>
                    
                    {/* Late Submissions */}
                    {student.late_submissions > 0 && (
                      <div className="text-center">
                        <div className="flex items-center gap-1 text-orange-600">
                          <Clock className="w-4 h-4" />
                          <span className="font-semibold">{student.late_submissions}</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Late</p>
                      </div>
                    )}
                    
                    {/* Last Activity */}
                    <div className="text-center hidden lg:block">
                      <div className="text-sm text-gray-600">
                        {student.last_activity ? new Date(student.last_activity).toLocaleDateString() : 'N/A'}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Last Active</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
